import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "../lib/auth";
import Monogram from "./Monogram";

const links = [
  { to: "/experiences", label: "Experiences" },
  { to: "/cities", label: "Cities" },
  { to: "/about", label: "About" },
  { to: "/rebel-notes", label: "Rebel Notes" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header data-testid="site-header"
      className="fixed top-0 inset-x-0 z-40 glass"
      style={{ padding: "0.75rem 1.5rem" }}>
      <div className="max-w-[1200px] mx-auto flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5 group font-body" style={{ color: "var(--rc-text)" }}>
          <Monogram size={32} className="transition-colors group-hover:text-[color:var(--rc-text)]" />
          <span className="font-display text-base sm:text-lg font-semibold tracking-tight" style={{ letterSpacing: "-0.015em" }}>
            Rebel<span className="text-[#2d7cf6]">&amp;Co</span>
          </span>
        </Link>
        <nav aria-label="Primary" className="hidden lg:flex items-center gap-6">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-xs font-body font-medium uppercase tracking-[0.08em] transition-colors ${isActive ? "text-[color:var(--rc-text)]" : "text-[color:var(--rc-text-muted)] hover:text-[color:var(--rc-text)]"}`
              }>{l.label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          {user ? (
            <>
              <Link to={user.role === "admin" ? "/admin" : "/dashboard"} data-testid="header-dashboard-btn"
                    className="hidden sm:inline-flex rc-btn rc-btn-outline-new" style={{ fontSize: "0.75rem", padding: "0.5rem 0.9rem", minHeight: 36 }}>
                {user.role === "admin" ? "Admin" : "Portal"}
              </Link>
              <button onClick={async () => { await logout(); nav("/"); }} data-testid="header-logout-btn"
                      className="hidden sm:inline-flex items-center gap-1 text-xs text-[color:var(--rc-text-muted)] hover:text-[color:var(--rc-text)] transition-colors">
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-btn"
                    className="hidden sm:inline-block text-xs font-body font-medium uppercase tracking-[0.08em] text-[color:var(--rc-text-muted)] hover:text-[color:var(--rc-text)]"
                    style={{ padding: "0.5rem" }}>Sign in</Link>
              <Link to="/experiences" data-testid="header-book-btn"
                    className="hidden sm:inline-flex rc-btn rc-btn-blue" style={{ fontSize: "0.75rem", padding: "0.5rem 0.9rem", minHeight: 36 }}>
                Book
              </Link>
            </>
          )}
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}
                  aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? "Close menu" : "Open menu"}
                  className="lg:hidden text-white rounded-md"
                  style={{ padding: "0.5rem", minWidth: 44, minHeight: 44 }}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div id="mobile-menu" data-testid="mobile-menu" className="lg:hidden mt-3 px-6 py-5 space-y-3 border-t border-[color:var(--rc-divider)]">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to}
              className={({ isActive }) =>
                `block text-sm font-body font-medium uppercase tracking-[0.08em] py-2 ${isActive ? "text-[color:var(--rc-text)]" : "text-[color:var(--rc-text-muted)]"}`
              } style={{ minHeight: 44, display: "flex", alignItems: "center" }}>{l.label}</NavLink>
          ))}
          <div className="flex gap-3 pt-3">
            {user ? (
              <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="rc-btn rc-btn-blue w-full">Portal</Link>
            ) : (
              <>
                <Link to="/login" className="rc-btn rc-btn-outline-new w-1/2">Sign in</Link>
                <Link to="/experiences" className="rc-btn rc-btn-blue w-1/2">Book</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
